import { GridRef } from "@/components/wijmo-grid/grid/grid";
import { CommonLocale, WordPhraseSearchLocale } from "@/locales/localeid";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import TitleListState from "@/modules/title-list";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	fetchLevels,
	getEndLevel,
	getRouteAccessLevel,
	getSelectedLevels,
	getSkillSelected,
	isAnyVersionSelected,
	sortLevelByLicenseAndUnit
} from "@/util/func";
import { Select, Input, Switch, Form, Button, Icon, Alert } from "ant-design-vue";
import { fmtMsg, ActionItem, MessageHelper, NotificationType, CurriculumType } from "commonui";
import { isNull } from "lodash";
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import TextHighlight from "vue-text-highlight";
import { WordPhraseSearchService } from "@/services/word-phrase-search-service";
import { WordPhraseSearchModel } from "@/models/word-phrase-search-model";
import { IWorkbookCell, WorkbookFont, WorkbookStyle } from "wijmo/wijmo.xlsx";
import { AllLanguageSkill } from "@/util/constants";
import PageTitle from "@/components/page-title/page-title.vue";
import { AccessLevel } from "@/util/enum";

interface RequiredParams {
	lsStartLevel: number;
	lsEndLevel: number;
	gsStartLevel: number;
	gsEndLevel: number;
	languageSkills: number[];
	gsVersionGroupId: string | null;
	lsVersionGroupId: string | null;
	isLemmaRequired: boolean;
	searchedWord: string;
}

@Component({
	components: {
		Select,
		Option: Select.Option,
		Grid,
		Input,
		AntdSwitch: Switch,
		TextHighlight,
		ActionItem,
		AppForm: Form,
		Item: Form.Item,
		Button,
		Icon,
		Alert,
		PageTitle
	}
})
export default class WordPhraseSearch extends Vue {
	@Ref() readonly gridCompRef!: GridRef;
	public titleListModule = getModule(TitleListState);
	public readonly allLanguageSkill = AllLanguageSkill;
	public startLevelSelected: number | null = null;
	public endLevelSelected: number | null = null;
	public skillSelected: number[] = [];
	public startLevel: VCSLevelModel[] = [];
	public endLevel: VCSLevelModel[] = [];
	public gridLoading: boolean = false;
	public isLemmaRequired: boolean = false;
	public phraseLemmas: string[] = [];
	public records: WordPhraseSearchModel[] = [];
	public units: VCSLevelModel[] = [];
	public wordPhraseSearchForm;
	public noVersion: boolean = false;
	public commonLocale = CommonLocale;
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	accessLevel = AccessLevel;

	get languageSkills(): LanguageSkillModel[] {
		return this.titleListModule.languageSkills;
	}
	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}
	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}
		this.wordPhraseSearchForm = this.$form.createForm(this, { name: "wordPhraseSearchForm" });
		this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion);
		this.skillSelected = [this.allLanguageSkill];
		this.selectStartAndEndLevel();
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
			this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion);
			this.selectStartAndEndLevel();
		} else {
			this.noVersion = true;
		}
	}

	setEndLevels(startLevelId: number) {
		this.endLevel = getEndLevel(this.units, startLevelId);

		if (this.endLevel.length > 0) {
			this.endLevelSelected = this.endLevel[0].id;
		} else {
			this.endLevel = [];
			this.endLevelSelected = null;
		}
	}

	onStartLevelChange(value: number) {
		this.setEndLevels(value);
	}

	setGridLoading(loading: boolean) {
		this.gridLoading = loading;
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
	}

	getRequiredParams(phraseText): RequiredParams | null {
		if (!isNull(this.startLevelSelected) && !isNull(this.endLevelSelected) && isAnyVersionSelected()) {
			const selectedLevels = getSelectedLevels(
				this.titleListModule.levels,
				this.startLevelSelected,
				this.endLevelSelected
			);
			const skills = this.skillSelected.includes(this.allLanguageSkill)
				? this.languageSkills.map((l) => l.id)
				: this.skillSelected;
			return {
				gsVersionGroupId:
					VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
				lsVersionGroupId:
					VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null,
				languageSkills: skills,
				isLemmaRequired: this.isLemmaRequired,
				searchedWord: phraseText,
				lsStartLevel: selectedLevels.lsStartUnit,
				lsEndLevel: selectedLevels.lsEndUnit,
				gsStartLevel: selectedLevels.gsStartUnit,
				gsEndLevel: selectedLevels.gsEndUnit
			};
		} else {
			MessageHelper.Message(
				NotificationType.Warning,
				this.localizedString(this.wordPhraseSearchLocale.WordPhraseSearchInvalidParamsError)
			);
			return null;
		}
	}

	get wordPhraseSearchLocale() {
		return WordPhraseSearchLocale;
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	get wordPhraseDefaultSort() {
		return {
			column: "title",
			ascending: true
		};
	}

	onDisplayClick(e) {
		if (e) {
			e.preventDefault();
		}

		this.wordPhraseSearchForm.validateFields((err, values) => {
			if (!err) {
				const params = this.getRequiredParams(values.phraseText);
				if (!isNull(params)) {
					this.getPhraseSearchInfo(params);
				}
			}
		});
	}

	get exportOptions() {
		return {
			formatCell: (cell: IWorkbookCell, binding: string, record: WordPhraseSearchModel) => {
				if (binding === "highlight") {
					if (cell.value === 1) {
						cell.value = "*";
					} else if (cell.value === 2) {
						cell.value = "*";
					} else {
						cell.value = "";
					}
				} else if (binding === "hit") {
					cell.style = new WorkbookStyle();
					cell.style.font = new WorkbookFont();
					cell.style.font.bold = true;
				} else if (binding === "rowCount") {
					cell.value = record.unitDisplayName;
				}
			},
			filename: "WordPhraseSearch Report"
		};
	}

	getLsMaxValue(lsRecords: WordPhraseSearchModel[]): number {
		if (!lsRecords || !lsRecords.length) {
			return 0;
		}

		return Math.max(...lsRecords.map((record) => record.unit));
	}

	getPhraseSearchInfo(params) {
		this.setGridLoading(true);
		WordPhraseSearchService.getWordPharseSearchList(params)
			.then((data: WordPhraseSearchModel[]) => {
				const lsRecords = data.filter((record) => record.curriculumType === CurriculumType.LittleSEED);
				const maxLsValue = this.getLsMaxValue(lsRecords);
				data.forEach((record) => {
					record.unitFactor =
						record.curriculumType === CurriculumType.LittleSEED ? record.unit : record.unit + maxLsValue;
				});
				this.records = data;
				this.setGridLoading(false);
			})
			.catch((ex) => {
				this.setGridLoading(false);
			});
	}

	selectStartAndEndLevel() {
		this.startLevel = sortLevelByLicenseAndUnit(this.units);
		if (this.startLevel.length > 0) {
			this.startLevelSelected = this.startLevel[0].id;
			this.setEndLevels(this.startLevelSelected);
		}
	}
}
